import { motion, AnimatePresence } from "framer-motion";
import { Shuffle, X, Loader2, Share2, Briefcase, FileText, Newspaper, Radio, Send, Mail } from "lucide-react";

interface StudioRemixModalProps {
  remixOpen: boolean;
  setRemixOpen: (v: boolean) => void;
  remixLoading: boolean;
  handleRemix: (targetFormat: string) => void;
}

const REMIX_FORMATS = [
  { label: "Tweet thread", icon: Share2 },
  { label: "Email", icon: Mail },
  { label: "Video script", icon: FileText },
  { label: "LinkedIn post", icon: Briefcase },
  { label: "Newsletter", icon: Newspaper },
  { label: "Podcast intro", icon: Radio },
  { label: "Cold email", icon: Send },
] as const;

export function StudioRemixModal({ remixOpen, setRemixOpen, remixLoading, handleRemix }: StudioRemixModalProps) {
  return (
    <AnimatePresence>
      {remixOpen && (
        <motion.div
          key="remix-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setRemixOpen(false); }}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 16 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 16 }}
            transition={{ duration: 0.2 }}
            className="bg-card border border-border rounded-2xl p-5 w-full max-w-sm shadow-2xl"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Shuffle className="w-4 h-4 text-primary" />
                <p className="text-sm font-semibold text-foreground">Remix to another format</p>
              </div>
              <button onClick={() => setRemixOpen(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Pick a target format — voice and message stay the same.
            </p>
            <div className="flex flex-wrap gap-2">
              {REMIX_FORMATS.map(({ label, icon: Icon }) => (
                <button
                  key={label}
                  type="button"
                  disabled={remixLoading}
                  onClick={() => handleRemix(label)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border/50 bg-secondary/20 text-xs font-medium text-muted-foreground hover:border-primary/40 hover:bg-primary/5 hover:text-primary transition-all disabled:opacity-40"
                >
                  {remixLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
                  → {label}
                </button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
